import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { message } from 'antd';
import { ApiResponse } from '../types';

const service = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 30000,
});

let refreshTokenPromise: Promise<any> | null = null;

const refreshTokenRequest = async () => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    const response = await service.post('/token/refresh', {}, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${refreshToken}`
      }
    });
    
    const { access_token, refresh_token } = response.data;
    
    if (!access_token || !refresh_token) {
      throw new Error('Invalid token response');
    }
    
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    
    return { access_token, refresh_token };
  } catch (error) {
    cleanToken();
    throw error;
  }
};

// 请求拦截器
service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (config.method?.toUpperCase() === 'OPTIONS') {
      return config;
    }
    
    if (config.url === '/token/refresh') {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        config.headers['Authorization'] = `Bearer ${refreshToken}`;
      }
    } else {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    console.log("Request error:", error);
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const res = response.data;
    if (res.status !== 200) {
      // message.error(res.message || 'Error');
      if (res.status === 1000) {
        message.error("请停止恶意访问，该行为已记录")
        return Promise.reject(new Error(res.message || 'Error'));
      }
      
      if (res.status === 50008 || res.status === 50012 || res.status === 50014) {
        message.error('You have been logged out, please log in again');
        return Promise.reject(new Error(res.message || 'Error'));
      }
    }
    return {
      ...response,
      data: res
    } as AxiosResponse<ApiResponse>;
  },
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && originalRequest.url === '/token/refresh') {
      cleanToken();
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        if (!refreshTokenPromise) {
          refreshTokenPromise = refreshTokenRequest().finally(() => {
            refreshTokenPromise = null;
          });
        }
        
        const { access_token } = await refreshTokenPromise;
        
        originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
        return service(originalRequest);
      } catch (error) {
        return Promise.reject(error);
      }
    }
    
    message.error(error.response?.data?.message || 'An error occurred');
    return Promise.reject(error);
  }
);

const cleanToken = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  window.location.href = '/login';
}

export default service;